/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiDownload, FiPackage, FiAward, FiTrendingUp, FiPieChart } from "react-icons/fi";
import PageContainer from "../../components/PageContainer";
import ResponsiveTable from "../../components/ResponsiveTable";
import UnifiedCard from "../../components/UnifiedCard";
import { useUI } from "../../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../../hooks/useTenantStorePermissions";
import NoAccess from "../../components/NoAccess";
import { formatNumber } from "utils/formatNumber";
import * as XLSX from "xlsx";

export default function ProductReport() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");

  const [loading, setLoading] = useState(true);
  const [topSellingProducts, setTopSellingProducts] = useState([]);
  const [topSellingBrands, setTopSellingBrands] = useState([]);
  const [activeTab, setActiveTab] = useState("selling");
  const [grandTotal, setGrandTotal] = useState(null);

  const tabs = [
    {
      id: "selling",
      label: isArabic ? "1- المنتج الأكثر مبيعا" : "1- Top Selling Product",
      icon: <FiTrendingUp />,
    },
    {
      id: "branded",
      label: isArabic ? "2- البراند الأكثر مبيعا" : "2- Top Selling Brand",
      icon: <FiAward />,
    },
  ];

  useEffect(() => {
    const fetchProductReport = async () => {
      if (!perms.viewProductReport) return;
      if (!tenantId || tenantId === -1) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const headers = {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        };

        // Fetch Top Selling Products
        const topProdRes = await fetch(`${baseUrl}/ProfitReports/product?tenantId=${tenantId}&itemId=-1`, { headers });
        if (topProdRes.ok) {
          const result = await topProdRes.json();
          setTopSellingProducts(Array.isArray(result) ? result : []);
        }

        // Fetch Top Selling Brands
        const topBrandRes = await fetch(`${baseUrl}/ProfitReports/brand?tenantId=${tenantId}&tagId=-1`, { headers });
        if (topBrandRes.ok) {
          const result = await topBrandRes.json();
          setTopSellingBrands(Array.isArray(result) ? result : []);
        }

      } catch (err) {
        console.error("Error fetching product report:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductReport();
  }, [tenantId, baseUrl, perms.viewProductReport]);

  // دالة التصدير إلى إكسل بناءً على التاب النشط
  const exportToExcel = () => {
    let dataToExport = [];
    let fileName = "";

    if (activeTab === "selling") {
      dataToExport = topSellingProducts.map(row => ({
        [isArabic ? "اسم المنتج" : "Product Name"]: row.ItemName || row.itemName || "—",
        [isArabic ? "سعر البيع" : "Sale Price"]: row.Price || row.price || 0,
        [isArabic ? "سعر الكلفة" : "Cost Price"]: row.CostPrice || row.costPrice || 0,
        [isArabic ? "الكمية الكلية" : "Total Qty"]: row.TotalQty || row.totalQty || 0,
        [isArabic ? "إجمالي الربح" : "Total Profit"]: row.TotalProfit || row.totalProfit || 0,
        [isArabic ? "صافي الربح" : "Net Profit"]: row.NetProfit || row.netProfit || 0,
      }));
      fileName = isArabic ? "تقرير_المنتجات_الأكثر_مبيعا" : "Top_Selling_Products";
    } else {
      dataToExport = topSellingBrands.map(row => ({
        [isArabic ? "اسم البراند" : "Brand Name"]: row.TagName || row.tagName || "—",
        [isArabic ? "إجمالي الربح" : "Total Profit"]: row.TotalProfit || row.totalProfit || 0,
      }));
      fileName = isArabic ? "تقرير_البراندات_الأكثر_مبيعا" : "Top_Selling_Brands";
    }

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (!perms.viewProductReport) {
    return <NoAccess />;
  }

  return (
    <PageContainer title={isArabic ? "تقرير المنتجات" : "Product Report"}>
      <h2 className="text-xl font-bold mb-4 px-2" style={{ color: colors.mainColor }}>
        {isArabic ? "المنتجات" : "Products"}
      </h2>

      {/* ===== الأزرار العلوية (Tabs) ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        {tabs.map((tab) => (
          <motion.div
            key={tab.id}
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveTab(tab.id)}
            className="cursor-pointer"
          >
            <UnifiedCard
              className={`transition-all duration-300 ${activeTab === tab.id ? 'ring-2 ring-offset-2' : ''}`}
              style={{
                borderColor: activeTab === tab.id ? colors.mainColor : 'transparent',
                backgroundColor: activeTab === tab.id
                  ? (isDark ? 'rgba(116, 0, 191, 0.1)' : 'rgba(116, 0, 191, 0.05)')
                  : (isDark ? 'rgba(30, 41, 59, 0.5)' : '#fff'),
              }}
            >
              <div className="flex flex-col items-center justify-center py-4 text-center">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                  style={{
                    backgroundColor: activeTab === tab.id ? colors.mainColor : (isDark ? '#334155' : '#f1f5f9'),
                    color: activeTab === tab.id ? '#fff' : colors.mainColor
                  }}
                >
                  {tab.icon}
                </div>
                <h3 className={`text-lg font-bold ${activeTab === tab.id ? (isDark ? 'text-white' : colors.mainColor) : (isDark ? 'text-slate-200' : 'text-slate-700')}`}>
                  {tab.label}
                </h3>
              </div>
            </UnifiedCard>
          </motion.div>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: colors.mainColor }}></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">

          {/* جدول المنتج الأكثر مبيعاً */}
          {activeTab === "selling" && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <div className="flex items-center gap-2 mb-4 px-2">
                <div className="p-2 rounded-lg" style={{ backgroundColor: `${colors.mainColor}20`, color: colors.mainColor }}>
                  <FiPackage size={20} />
                </div>
                <h3 className="text-lg font-bold" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                  {isArabic ? "1- المنتج الأكثر مبيعا" : "1- Top Selling Product"}
                </h3>
              </div>
              <ResponsiveTable
                columns={[
                  { header: isArabic ? "اسم المنتج" : "Product Name", sortKey: "ItemName" },
                  { header: isArabic ? "سعر البيع" : "Sale Price", sortKey: "Price", align: "center" },
                  { header: isArabic ? "سعر الكلفة" : "Cost Price", sortKey: "CostPrice", align: "center" },
                  { header: isArabic ? "الكمية الكلية" : "Total Qty", sortKey: "TotalQty", align: "center" },
                  { header: isArabic ? "إجمالي الربح" : "Total Profit", sortKey: "TotalProfit", align: "center" },
                  { header: isArabic ? "صافي الربح" : "Net Profit", sortKey: "NetProfit", align: "center" },
                ]}
                data={topSellingProducts}
                headerActions={
                  <button
                    onClick={exportToExcel}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-white transition-all shadow-md text-sm"
                    style={{ backgroundColor: colors.mainColor }}
                  >
                    <FiDownload size={16} /> {isArabic ? "تنزيل إكسل" : "Download Excel"}
                  </button>
                }
                renderRow={(row) => (
                  <>
                    <td className="py-3 px-4 font-medium" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                      {row.ItemName || row.itemName || "—"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {formatNumber(row.Price || row.price || 0)} {isArabic ? "د.ع" : "IQD"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {formatNumber(row.CostPrice || row.costPrice || 0)} {isArabic ? "د.ع" : "IQD"}
                    </td>
                    <td className="py-3 px-4 text-center font-bold" style={{ color: colors.mainColor }}>
                      {row.TotalQty || row.totalQty || 0}
                    </td>
                    <td className="py-3 px-4 text-center font-bold" style={{ color: colors.blue }}>
                      {formatNumber(row.TotalProfit || row.totalProfit || 0)} {isArabic ? "د.ع" : "IRQ"}
                    </td>
                    <td className="py-3 px-4 text-center font-bold" style={{ color: colors.green }}>
                      {formatNumber(row.NetProfit || row.netProfit || 0)} {isArabic ? "د.ع" : "IRQ"}
                    </td>
                  </>
                )}
              />
            </motion.div>
          )}

          {/* جدول البراند الأكثر مبيعاً */}
          {activeTab === "branded" && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <div className="flex items-center gap-2 mb-4 px-2">
                <div className="p-2 rounded-lg" style={{ backgroundColor: `${colors.gold}20`, color: colors.gold }}>
                  <FiAward size={20} />
                </div>
                <h3 className="text-lg font-bold" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                  {isArabic ? "2- البراند الأكثر مبيعا" : "2- Top Selling Brand"}
                </h3>
              </div>
              <ResponsiveTable
                columns={[
                  { header: isArabic ? "اسم البراند" : "Brand Name", sortKey: "TagName" },
                  { header: isArabic ? "إجمالي الربح" : "Total Profit", sortKey: "TotalProfit", align: "center" },
                ]}
                data={topSellingBrands}
                headerActions={
                  <button
                    onClick={exportToExcel}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-white transition-all shadow-md text-sm"
                    style={{ backgroundColor: colors.mainColor }}
                  >
                    <FiDownload size={16} /> {isArabic ? "تنزيل إكسل" : "Download Excel"}
                  </button>
                }
                renderRow={(row) => (
                  <>
                    <td className="py-3 px-4 font-medium" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                      {row.TagName || row.tagName || "—"}
                    </td>
                    <td className="py-3 px-4 text-center font-bold" style={{ color: colors.green }}>
                      {formatNumber(row.TotalProfit || row.totalProfit || 0)} {isArabic ? "د.ع" : "IRQ"}
                    </td>
                  </>
                )}
              />
            </motion.div>
          )}
        </div>
      )}
    </PageContainer>
  );
}